import { useCallback, useEffect, useState } from 'react';
import type { MelaIdentity } from '@mela/shared-types';
import { apiClient } from './client';
import { useSession } from './useSession';

interface UseMelaIdentityResult {
  identity: MelaIdentity | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  update: (patch: Partial<MelaIdentity>) => Promise<MelaIdentity>;
}

export function useMelaIdentity(): UseMelaIdentityResult {
  const { user, loading: sessionLoading } = useSession();
  const [identity, setIdentity] = useState<MelaIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setIdentity(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const me = await apiClient.getMe();
      setIdentity(me);
      setError(null);
    } catch (e) {
      const code = (e as { code?: string })?.code ?? 'UNKNOWN';
      setError(code);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (sessionLoading) return;
    refresh();
  }, [sessionLoading, refresh]);

  const update = useCallback(
    async (patch: Partial<MelaIdentity>) => {
      const updated = await apiClient.updateMe(patch);
      setIdentity(updated);
      return updated;
    },
    []
  );

  return { identity, loading: loading || sessionLoading, error, refresh, update };
}
